import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { Order, LogElement } from '@models/order';
import { ProductElement } from '@models/product';
import { VendorOrders } from '@models/vendor-order';
import { AuthService } from '@services/auth.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse } from '@classes/api-response';
import { formatDate } from '@angular/common';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private base_url = environment.base_url + '/order';

  private orderSource = new Subject<Order>();
  private orderImageSource = new Subject<Object>();
  order$ = this.orderSource.asObservable();
  orderImages$ = this.orderImageSource.asObservable();

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public http: HttpClient,
    private auth: AuthService,
    private vendorOrderService: VendorOrderService
  ) {}

  private invalidateProductHistory(
    request?: Order | Partial<Order> | Order[] | null,
    response?: Order | Partial<Order> | Order[] | null
  ): void {
    const invalidateOne = (order?: Order | Partial<Order> | null) => {
      this.vendorOrderService.invalidateProductHistoryForOrder(order);
    };

    if (Array.isArray(request)) {
      request.forEach(invalidateOne);
    } else {
      invalidateOne(request);
    }

    if (Array.isArray(response)) {
      response.forEach(invalidateOne);
    } else {
      invalidateOne(response);
    }
  }

  queryOrders(query: String): Observable<Object> {
    var store = this.auth.getStore();
    return this.http
      .get(`${this.base_url}/store/${store}/query/${query}`)
      .pipe(map((res) => res));
  }

  getOrder(orderId: String): Observable<Order> {
    return this.getOrderById(orderId);
  }

  getPatientOrders(
    patientId: String,
    section: String,
    status: string = null
  ): Observable<any> {
    return this.http
      .get(
        `${this.base_url}/getPatientOrders/${section}/${patientId}${
          status ? `/${status}` : ''
        }`
      )
      .pipe(map((res: ApiResponse<Order[]>) => res.orders));
  }

  getNewOrders(section: String): Observable<VendorOrders[]> {
    var store = this.auth.getStore();
    const sectionSlug = section.toString().trim().toLowerCase().replace(/\s+/g, '-');
    return this.http
      .get(`${this.base_url}/getNewOrders/store/${store}/${sectionSlug}`)
      .pipe(map((res: ApiResponse<VendorOrders[]>) => res.orders));
  }

  getDispensedOrders(): Observable<VendorOrders[]> {
    var store = this.auth.getStore();
    return this.http
      .get(`${this.base_url}/getDispensedOrders/store/${store}`)
      .pipe(map((res: ApiResponse<VendorOrders[]>) => res.orders));
  }

  getProductOrders(productId: string): Observable<Order[]> {
    return this.http
      .get(`${this.base_url}/getProductOrders/${productId}`)
      .pipe(map((res: ApiResponse<Order[]>) => res.orders));
  }

  getOrderImages(orderId: String): Observable<any> {
    return this.http
      .get(`${this.base_url}/getImages/${orderId}`)
      .pipe(map((res) => this.setOrderImages(res)));
  }

  getPartsOrders(vendorId: string): Observable<any[]> {
    return this.http
      .get(`${this.base_url}/getPartsOrders/${vendorId}`)
      .pipe(map((res: ApiResponse<any[]>) => res.orders));
  }

  setPatientOrders(res) {
    return res;
  }

  setOrder(res) {
    return res;
  }

  doneUpdatingOrder(res) {
    return res;
  }

  doneDeletingOrder(res) {
    return res;
  }

  setOrderImages(res) {
    return res;
  }

  addOrder(order: Order): Observable<any> {
    let body = order;
    return this.http.post(`${this.base_url}`, body).pipe(
      tap((res: ApiResponse<Order[]>) => {
        this.invalidateProductHistory(order, res.orders);
      }),
      map((res: ApiResponse<Order[]>) => {
        return res.orders;
      })
    );
  }

  addOrdersFromTmp(orders: Order[]): Observable<boolean> {
    let body = orders;
    return this.http.post(`${this.base_url}/addFromTmp`, body).pipe(
      tap((res: ApiResponse<Order[]>) => {
        this.invalidateProductHistory(orders, res.orders);
      }),
      map((res) => this.setOrder(res))
    );
  }

  addOrders(orders: Order[]): Observable<any> {
    let body = orders;
    return this.http.post(`${this.base_url}`, body).pipe(
      tap((res: ApiResponse<Order[]>) => {
        this.invalidateProductHistory(orders, res.orders);
      }),
      map((res) => this.setOrder(res))
    );
  }

  updateOrder(orderId: any, order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/${orderId}`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res) => {
        return this.doneUpdatingOrder(res);
      })
    );
  }

  updateOrderStatus(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/status`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateOrderDispensed(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/dispensed`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateOrderStatusAndLog(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/statusLog`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateOrderFull(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/full`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateOrderTaxAndShipping(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/taxAndShipping`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateOrderVendorOrder(order: Order): Observable<Order> {
    let body = order;
    return this.http.put(`${this.base_url}/vendorOrder`, body).pipe(
      tap((res: ApiResponse<Order>) => {
        this.invalidateProductHistory(order, res.order);
      }),
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  updateImages(orderId: any, images: any): Observable<Order> {
    let body = images;
    return this.http.put(`${this.base_url}/${orderId}/images`, body).pipe(
      map((res: ApiResponse<Order>) => {
        return res.order;
      })
    );
  }

  deleteImage(orderId: any, imageId: any): Observable<any> {
    return this.http
      .delete(`${this.base_url}/${orderId}/image/${imageId}`)
      .pipe(map((res) => res));
  }

  deleteOrder(
    orderId: any,
    audit?: { reason: string; notes?: string; initials?: string }
  ): Observable<Order> {
    return this.http
      .delete(`${this.base_url}/${orderId}`, {
        ...httpOptions,
        body: ({
          reason: audit?.reason || '',
          notes: audit?.notes || '',
          initials: audit?.initials || this.auth.currentUser?.initials || '',
        }),
      })
      .pipe(
        tap((res: ApiResponse<Order>) => {
          this.invalidateProductHistory(null, res.order);
        }),
        map((res) => {
          return this.doneDeletingOrder(res);
        })
      );
  }

  returnOrder(
    order: Order,
    credit: any,
    returnToInventory: boolean = true
  ): Observable<Order> {
    const params = { returnToInventory: String(returnToInventory !== false) };
    return this.http
      .put(`${this.base_url}/return/credit/${credit}`, order, { params })
      .pipe(
        tap((res: ApiResponse<Order>) => {
          this.invalidateProductHistory(order, res.order);
        }),
        map((res: ApiResponse<Order>) => {
          return res.order;
        })
      );
  }

  setOrderAsPrescription(orderId: any): Observable<Order> {
    return this.http
      .put(`${this.base_url}/setAsPrescription/${orderId}`, httpOptions)
      .pipe(
        tap((res: ApiResponse<Order>) => {
          this.invalidateProductHistory(null, res.order);
        }),
        map((res) => {
          return this.doneDeletingOrder(res);
        })
      );
  }

  addStockOrder(
    product: ProductElement,
    type: string,
    status: string
  ): Observable<Order> {
    let newOrder = this.getNewOrder(
      product,
      'ORDER FOR STOCK',
      type,
      true,
      'REORDER',
      status
    );
    return this.addOrder(newOrder);
  }

  addReturnOrder(product: ProductElement, type: string): Observable<Order> {
    let newOrder = this.getNewOrder(
      product,
      'RETURNING',
      type,
      false,
      'RETURN',
      'RETURNING'
    );
    return this.addOrder(newOrder);
  }

  getOrderById(orderId: String): Observable<Order> {
    return this.http.get<Order>(`${this.base_url}/v2/${orderId}`);
  }

  /**
   * Atomically reserves the next invoice number for the current store.
   * Used when opening a new Add Order page so concurrent users don't collide.
   */
  reserveInvoiceNumber(): Observable<string> {
    const store = this.auth.getStore();
    return this.http
      .get(`${this.base_url}/reserveInvoiceNumber/store/${store}`)
      .pipe(
        map((res: any) => {
          const invoiceNumber = String(res?.invoiceNumber || '').trim();
          if (!invoiceNumber || res?.success === false) {
            throw new Error(
              res?.message || 'Failed to reserve invoice number'
            );
          }
          return invoiceNumber;
        })
      );
  }

  updateOrderById(orderId: string, order: Partial<Order>): Observable<Order> {
    const body = order;
    return this.http.put<Order>(`${this.base_url}/v2/${orderId}`, body).pipe(
      tap((responseOrder) => {
        this.invalidateProductHistory(order, responseOrder);
      })
    );
  }

  private getNewOrder(product, itemNote, type, restockOrder, todo, status) {
    product.frame.frameSource = 'STOCK';

    let logItem = new LogElement();
    logItem.initials = this.auth.currentUser.initials;
    logItem.user = this.auth.currentUser._id;
    logItem.item = itemNote;
    logItem.notes = '';
    logItem.date = Date.now().toString();

    const newOrder: Order = {
      _id: '',
      invoiceNumber: '',
      type: type == 'RX' ? 'O' : 'N',
      restockOrder: restockOrder,
      patient: null,
      date: formatDate(
        Date.now(),
        'yyyy/MM/dd HH:mm:ss.SSS',
        this.locale,
        'PST'
      ),
      dateAdded: formatDate(
        Date.now(),
        'yyyy/MM/dd HH:mm:ss.SSS',
        this.locale,
        'PST'
      ),
      dateUpdated: formatDate(
        Date.now(),
        'yyyy/MM/dd HH:mm:ss.SSS',
        this.locale,
        'PST'
      ),
      archived: false,
      notes: '',
      todo: todo,
      images: [],
      products: [product],
      status: status,
      user: this.auth.currentUser._id,
      log: [logItem],
      store: this.auth.getStore(),
    };

    return newOrder;
  }
}
